/**
 * Early-access API client — wraps the public /api/early-access endpoints.
 *
 * These calls are made outside the normal auth flow (by definition, the user
 * isn't logged in yet when they're being gated), so we use apiClient but
 * don't care about the access token / CSRF plumbing. The cookie travels
 * automatically via `withCredentials: true`.
 */

import { apiClient } from './apiClient';

/** Reason codes returned by GET /api/early-access/status. */
export type EarlyAccessStatusReason = 'no_cookie' | 'expired' | 'revoked' | 'invalid' | 'team';

export interface EarlyAccessStatus {
  active: boolean;
  /** Unix-ms; null means infinite grant. Only populated when active. */
  expiresAtMs?: number | null;
  /** Populated when !active OR when active via the team-header bypass. */
  reason?: EarlyAccessStatusReason;
  /** True iff this is a team-header grant (no DB row). */
  team?: boolean;
}

export interface RedeemSuccess {
  ok: true;
  expiresAtMs: number | null;
}
export interface RedeemFailure {
  ok: false;
  reason: 'invalid_or_expired';
}

/** POST /api/early-access/redeem — trade a URL token for a cookie. */
export async function redeemEarlyAccessToken(
  token: string,
): Promise<RedeemSuccess | RedeemFailure> {
  try {
    return await apiClient.post<RedeemSuccess>('/early-access/redeem', { token });
  } catch (err) {
    // 410 Gone is our "link no longer usable" code. Rate-limit and validation
    // errors (400 / 429) are bucketed into the same UX — invalid or expired.
    const status = (err as { response?: { status?: number } })?.response?.status;
    const body = (err as { response?: { data?: { reason?: string } } })?.response?.data;
    if (status === 410 && body?.reason === 'invalid_or_expired') {
      return { ok: false, reason: 'invalid_or_expired' };
    }
    // Network or unexpected — surface as invalid so the UI shows the
    // link-expired page rather than hanging.
    return { ok: false, reason: 'invalid_or_expired' };
  }
}

/** GET /api/early-access/status — check current cookie against the backend. */
export async function getEarlyAccessStatus(): Promise<EarlyAccessStatus> {
  try {
    return await apiClient.get<EarlyAccessStatus>('/early-access/status');
  } catch {
    // Treat network errors as "no cookie" to avoid locking people out of
    // the login page on transient issues. If they HAD a cookie the server
    // will reconfirm on the next call.
    return { active: false, reason: 'no_cookie' };
  }
}
